import { CheckCircleFilled, CopyOutlined, LoadingOutlined } from '@ant-design/icons';
import type { MessageDescriptor } from '@lingui/core';
import { msg, t } from '@lingui/core/macro';
import { useLingui } from '@lingui/react';
import { Trans } from '@lingui/react/macro';
import { Tabs } from 'antd';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDismissibleLayer } from '../hooks/useDismiss';
import { usePresence } from '../hooks/usePresence';
import { errorMessage } from '../i18n/errorMessages';
import type { HostedAgentPlacement } from '../lib/api';
import * as api from '../lib/api';
import { copyToClipboard } from '../lib/clipboard';
import {
  AGENT_PROVIDERS,
  AGENT_PROVIDER_LABELS,
  type AgentProvider,
  type Platform,
  buildAgentDisconnectCommandParts,
  providerHasPlatformDifference,
  resolveAgentProvider,
} from './AgentConnectResult';

const DISCONNECT_COPY: Record<
  AgentProvider,
  {
    stepLabel: MessageDescriptor;
    doneLabel: (name: string) => MessageDescriptor;
    instruction: (name: string) => MessageDescriptor;
    skipHint: MessageDescriptor;
  }
> = {
  openclaw: {
    stepLabel: msg`Remove the Borgee plugin`,
    doneLabel: (name) => msg`${name} plugin removed`,
    instruction: (name) => msg`Remove the Borgee plugin from ${name}:`,
    skipHint: msg`If you skip this step, the plugin will fail to connect after deletion.`,
  },
  claude: {
    stepLabel: msg`Stop the agent process`,
    doneLabel: (name) => msg`${name} disconnected`,
    instruction: (name) => msg`Stop ${name} on your machine:`,
    skipHint: msg`If you can't stop the process, you can skip this step — the agent will be disconnected automatically after deletion.`,
  },
  copilot: {
    stepLabel: msg`Stop the agent process`,
    doneLabel: (name) => msg`${name} disconnected`,
    instruction: (name) => msg`Stop ${name} on your machine:`,
    skipHint: msg`If you can't stop the process, you can skip this step — the agent will be disconnected automatically after deletion.`,
  },
  codex: {
    stepLabel: msg`Stop the agent process`,
    doneLabel: (name) => msg`${name} disconnected`,
    instruction: (name) => msg`Stop ${name} on your machine:`,
    skipHint: msg`If you can't stop the process, you can skip this step — the agent will be disconnected automatically after deletion.`,
  },
  dsh: {
    stepLabel: msg`Stop DSH Web`,
    doneLabel: (name) => msg`${name} disconnected`,
    instruction: () => msg`Stop the DSH Web process that hosts the Borgee connector:`,
    skipHint: msg`If you can't stop DSH Web, you can skip this step — the agent will be disconnected automatically after deletion.`,
  },
  praestoclaw: {
    stepLabel: msg`Stop the agent process`,
    doneLabel: (name) => msg`${name} disconnected`,
    instruction: (name) => msg`Stop ${name} on your machine:`,
    skipHint: msg`If you can't stop the process, you can skip this step — the agent will be disconnected automatically after deletion.`,
  },
  hermes: {
    stepLabel: msg`Stop the bridge process`,
    doneLabel: () => msg`borgee-hermes-plugin disconnected`,
    instruction: () => msg`Stop the borgee-hermes-plugin on your machine:`,
    skipHint: msg`If you can't stop the process, you can skip this step — the agent will be disconnected automatically after deletion.`,
  },
  kosmos: {
    stepLabel: msg`Disconnect the Borgee remote channel`,
    doneLabel: (name) => msg`${name} disconnected`,
    instruction: () =>
      msg`In Kosmos Desktop, open Settings → Remote Channel → Borgee and click Disconnect:`,
    skipHint: msg`If you can't reach Kosmos now, you can skip this step — the agent will be disconnected automatically after deletion.`,
  },
};

interface Props {
  agentId: string;
  agentName: string;
  agentState?: string;
  lastConnectedPluginId?: string;
  hostedPlacement?: HostedAgentPlacement | null;
  hostedComputerName?: string | null;
  onClose: () => void;
  onDeleted: () => void;
}

const BADGE_DONE: React.CSSProperties = {
  width: 18,
  height: 18,
  borderRadius: '50%',
  background: 'var(--color-success, #22c55e)',
  color: '#fff',
  fontSize: 11,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  marginTop: 1,
};
const BADGE_ACTIVE: React.CSSProperties = {
  width: 18,
  height: 18,
  borderRadius: '50%',
  border: '2px solid var(--accent-primary, #5865f2)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  marginTop: 1,
  fontSize: 11,
  color: 'var(--accent-primary, #5865f2)',
};
const BADGE_FUTURE: React.CSSProperties = {
  width: 18,
  height: 18,
  borderRadius: '50%',
  border: '2px solid var(--text-tertiary, #ccc)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  marginTop: 1,
  fontSize: 11,
  color: 'var(--text-tertiary, #999)',
};

/**
 * One screen of the decommission modal: the scrim covering the app, and the
 * card drawn in it.
 *
 * The scrim is a dismissible layer, and it is the scrim rather than the card
 * that is the layer's box. That is what makes the press which closes this modal
 * belong to it: a press on the scrim lands inside the layer, so nothing the
 * modal was raised over — a floating thread panel, a menu — is dismissed by the
 * same press. Escape stops here for the same reason.
 *
 * `busy` keeps the modal on the stack and stops both gestures acting: the work
 * they would be cancelling has already gone out, and it is not the modal's to
 * call back. Unmounting mid-request would take the failure report with it —
 * the delete's error has nowhere to land — and leave the channel-removal loop,
 * which is a plain `for` over the agent's channels rather than anything tied to
 * this component, still removing the agent from the rest of them.
 */
export function DecommissionModalSurface({
  maxWidth,
  busy = false,
  onClose,
  children,
}: {
  maxWidth: number;
  busy?: boolean;
  onClose: () => void;
  children: React.ReactNode;
}) {
  const { i18n } = useLingui();
  const scrimRef = useRef<HTMLDivElement>(null);

  useDismissibleLayer({ active: true, within: scrimRef, onEscape: busy ? undefined : onClose });

  return (
    <div ref={scrimRef} className="admin-modal" onClick={busy ? undefined : onClose}>
      <div
        className="admin-modal-content"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth }}
      >
        {children}
      </div>
    </div>
  );
}

export default function DecommissionAgentModal({
  agentId,
  agentName,
  agentState,
  lastConnectedPluginId,
  hostedPlacement: hostedPlacementProp,
  hostedComputerName: hostedComputerNameProp,
  onClose,
  onDeleted,
}: Props) {
  const { i18n } = useLingui();
  const [step, setStep] = useState<'confirm' | 'provider' | 'flow'>('confirm');
  const [hostedPlacementState, setHostedPlacementState] = useState<HostedAgentPlacement | null>(
    hostedPlacementProp ?? null,
  );
  const [hostedComputerNameState, setHostedComputerNameState] = useState<string | null>(
    hostedComputerNameProp ?? null,
  );
  const hostedPlacement = hostedPlacementProp ?? hostedPlacementState;
  const hostedComputerName = hostedComputerNameProp ?? hostedComputerNameState;
  const isHostedAgent = Boolean(hostedPlacement);
  const [selectedProvider, setSelectedProvider] = useState<AgentProvider>(
    () => hostedPlacement?.provider ?? resolveAgentProvider(agentId, lastConnectedPluginId),
  );
  const hostedMachineLabel =
    hostedComputerName ?? hostedPlacement?.node_id ?? t(i18n)`this computer`;
  const continueStep = () => setStep(isHostedAgent ? 'flow' : 'provider');

  useEffect(() => {
    if (hostedPlacementProp) {
      setHostedPlacementState(hostedPlacementProp);
      setHostedComputerNameState(hostedComputerNameProp ?? null);
      return;
    }
    let cancelled = false;
    const loadHostedDetails = async () => {
      try {
        const { blob } = await api.fetchAgentConfig(agentId);
        if (cancelled) return;
        const hosted = blob.hosted ?? null;
        setHostedPlacementState(hosted);
        if (!hosted) {
          setHostedComputerNameState(null);
          return;
        }
        const nodes = await api.fetchRemoteNodes();
        if (cancelled) return;
        setHostedComputerNameState(
          nodes.find((node) => node.id === hosted.node_id)?.machine_name ?? hosted.node_id,
        );
      } catch {
        if (!cancelled) {
          setHostedPlacementState(null);
          setHostedComputerNameState(null);
        }
      }
    };
    void loadHostedDetails();
    return () => {
      cancelled = true;
    };
  }, [agentId, hostedComputerNameProp, hostedPlacementProp]);

  useEffect(() => {
    if (hostedPlacement?.provider) {
      setSelectedProvider(hostedPlacement.provider);
    }
  }, [hostedPlacement]);

  if (step === 'confirm') {
    return (
      <DecommissionModalSurface maxWidth={420} onClose={onClose}>
        <h3>
          {isHostedAgent
            ? t(i18n)`Delete hosted agent ${agentName}?`
            : t(i18n)`Delete ${agentName}?`}
        </h3>
        <p style={{ fontSize: 13, margin: '16px 0', color: 'var(--text-secondary)' }}>
          {isHostedAgent ? (
            <>
              <Trans>
                This will remove <b>{agentName}</b> from all channels, stop{' '}
                <b>{hostedMachineLabel}</b> from hosting it, and permanently delete the agent. This
                will not delete the computer or affect any other hosted agents on it.
              </Trans>
            </>
          ) : (
            <>
              <Trans>
                This will remove <b>{agentName}</b> from all channels, disconnect it, and
                permanently delete the agent. This cannot be undone.
              </Trans>
            </>
          )}
        </p>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button className="btn btn-sm" onClick={onClose}>
            <Trans>Cancel</Trans>
          </button>
          <button
            className="btn btn-sm"
            style={{
              background: 'var(--text-danger, #d32f2f)',
              color: '#fff',
              borderColor: 'var(--text-danger, #d32f2f)',
            }}
            onClick={continueStep}
          >
            <Trans>Continue</Trans>
          </button>
        </div>
      </DecommissionModalSurface>
    );
  }

  if (step === 'provider') {
    const cardStyle = (selected: boolean): React.CSSProperties => ({
      padding: 16,
      borderRadius: 8,
      border: `2px solid ${selected ? 'var(--accent-primary, #5865f2)' : 'var(--border-color, #e0e0e0)'}`,
      cursor: 'pointer',
      background: selected ? 'var(--bg-hover, #f0f0ff)' : 'var(--bg-primary, #fff)',
      transition: 'all 0.15s',
    });
    return (
      <DecommissionModalSurface maxWidth={420} onClose={onClose}>
        <h3>
          <Trans>How is {agentName} connected?</Trans>
        </h3>
        {/* Seeded from resolveAgentProvider, which can answer with any provider,
            so the cards are the provider list itself — a provider with no card
            here leaves the owner no way to answer truthfully. */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 16 }}>
          {AGENT_PROVIDERS.map((provider) => (
            <div
              key={provider}
              style={cardStyle(selectedProvider === provider)}
              onClick={() => setSelectedProvider(provider)}
            >
              <div style={{ fontWeight: 600, fontSize: 14 }}>{AGENT_PROVIDER_LABELS[provider]}</div>
            </div>
          ))}
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
          <button className="btn btn-sm" onClick={() => setStep('confirm')}>
            <Trans>← Back</Trans>
          </button>
          <button className="btn btn-sm btn-primary" onClick={() => setStep('flow')}>
            <Trans>Next</Trans>
          </button>
        </div>
      </DecommissionModalSurface>
    );
  }

  return (
    <DecommissionFlow
      agentId={agentId}
      agentName={agentName}
      agentState={agentState}
      provider={selectedProvider}
      hostedPlacement={hostedPlacement}
      hostedComputerName={hostedComputerName}
      onClose={onClose}
      onDeleted={onDeleted}
    />
  );
}

function DecommissionFlow({
  agentId,
  agentName,
  agentState,
  provider,
  hostedPlacement,
  hostedComputerName,
  onClose,
  onDeleted,
}: Omit<Props, 'lastConnectedPluginId'> & { provider: AgentProvider }) {
  const { i18n } = useLingui();
  const [channels, setChannels] = useState<{ id: string; name: string }[]>([]);
  const [removedChannels, setRemovedChannels] = useState<Set<string>>(new Set());
  const [channelsLoading, setChannelsLoading] = useState(true);
  const [removing, setRemoving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [hostedDeleteRequested, setHostedDeleteRequested] = useState(false);
  const [hostedDeleteSettled, setHostedDeleteSettled] = useState(false);
  const [error, setError] = useState<MessageDescriptor | null>(null);
  const [platform, setPlatform] = useState<Platform>('linux');
  const removingRef = useRef(false);

  const presence = usePresence(agentId);
  const effectiveState = presence?.state ?? agentState ?? 'offline';
  const isOffline = effectiveState === 'offline';

  const allRemoved = channels.length === 0 || removedChannels.size === channels.length;
  const step1Done = allRemoved && !channelsLoading;
  const isHostedAgent = Boolean(hostedPlacement);
  const step2Done = isHostedAgent ? hostedDeleteRequested : step1Done && isOffline;
  const step3Done = isHostedAgent ? hostedDeleteSettled : false;

  const hasPlatformDiff = providerHasPlatformDifference(provider);
  const disconnectParts = buildAgentDisconnectCommandParts(i18n, provider, agentName, platform);
  const copy = DISCONNECT_COPY[provider];
  const hostedMachineLabel =
    hostedComputerName ?? hostedPlacement?.node_id ?? t(i18n)`this computer`;
  const step2Label = isHostedAgent ? t(i18n)`Delete hosted agent` : i18n._(copy.stepLabel);
  const step2DoneLabel = isHostedAgent
    ? t(i18n)`${agentName} deleted`
    : i18n._(copy.doneLabel(agentName));
  const waitingCopy = isHostedAgent
    ? t(i18n)`Refreshing hosted agents on ${hostedMachineLabel}...`
    : t(i18n)`Waiting for ${agentName} to go offline...`;
  const hostedDeletePending = isHostedAgent && hostedDeleteRequested && !hostedDeleteSettled;

  useEffect(() => {
    api
      .getAgentChannels(agentId)
      .then((chs) => {
        setChannels(chs);
        setChannelsLoading(false);
      })
      .catch(() => setChannelsLoading(false));
  }, [agentId]);

  useEffect(() => {
    if (!isHostedAgent || !hostedPlacement || !hostedDeleteRequested || hostedDeleteSettled) return;
    let cancelled = false;
    let retryTimer: number | undefined;

    const pollHostedAgents = async () => {
      try {
        const agents = await api.fetchRemoteNodeHostedAgents(hostedPlacement.node_id);
        if (cancelled) return;
        if (!agents.some((agent) => agent.id === agentId)) {
          setHostedDeleteSettled(true);
          return;
        }
      } catch {
        if (cancelled) return;
      }
      retryTimer = window.setTimeout(() => {
        void pollHostedAgents();
      }, 1000);
    };

    void pollHostedAgents();
    return () => {
      cancelled = true;
      if (retryTimer !== undefined) {
        window.clearTimeout(retryTimer);
      }
    };
  }, [agentId, hostedDeleteRequested, hostedDeleteSettled, hostedPlacement, isHostedAgent]);

  const handleStartRemoval = useCallback(async () => {
    if (removingRef.current) return;
    removingRef.current = true;
    setRemoving(true);
    for (const ch of channels) {
      if (removedChannels.has(ch.id)) continue;
      try {
        await api.removeChannelMember(ch.id, agentId);
      } catch {
        // Some channels (e.g. #general) reject member removal — skip and
        // let handleDeleteAgent clean up via direct DB removal.
      }
      setRemovedChannels((prev) => new Set(prev).add(ch.id));
    }
    setRemoving(false);
    removingRef.current = false;
  }, [agentId, channels, removedChannels]);

  const handleDelete = useCallback(async () => {
    setDeleting(true);
    setError(null);
    try {
      await api.deleteAgent(agentId);
      if (isHostedAgent) {
        setHostedDeleteRequested(true);
        setDeleting(false);
        return;
      }
      onDeleted();
    } catch (err) {
      setError(errorMessage(err, msg`Failed to delete`));
      setDeleting(false);
    }
  }, [agentId, isHostedAgent, onDeleted]);

  const handleHostedDone = useCallback(() => {
    onDeleted();
  }, [onDeleted]);

  return (
    <DecommissionModalSurface maxWidth={520} busy={removing || deleting} onClose={onClose}>
      <h3>
        {isHostedAgent
          ? t(i18n)`Delete hosted agent ${agentName}`
          : t(i18n)`Decommission ${agentName}`}
      </h3>

      {error && (
        <p style={{ color: 'var(--text-danger, #d32f2f)', fontSize: 13, margin: '12px 0' }}>
          {i18n._(error)}
        </p>
      )}

      {/* Step 1: Remove from channels */}
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 10,
          marginTop: 16,
          marginBottom: 8,
        }}
      >
        {step1Done ? (
          <>
            <span style={BADGE_DONE}>&#10003;</span>
            <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--color-success, #22c55e)' }}>
              <Trans>Removed from all channels</Trans>
            </span>
          </>
        ) : (
          <>
            <span style={BADGE_ACTIVE}>1</span>
            <span style={{ fontSize: 13, fontWeight: 500 }}>
              <Trans>Remove from channels</Trans>
            </span>
            {!channelsLoading && channels.length > 0 && !removing && (
              <button
                className="btn btn-sm btn-primary"
                style={{ marginLeft: 'auto', fontSize: 11, padding: '2px 10px' }}
                onClick={handleStartRemoval}
              >
                <Trans>Start</Trans>
              </button>
            )}
          </>
        )}
      </div>
      {!step1Done && (
        <div style={{ marginLeft: 28 }}>
          {channelsLoading ? (
            <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
              <LoadingOutlined aria-hidden="true" /> <Trans>Loading...</Trans>
            </span>
          ) : channels.length === 0 ? (
            <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
              <Trans>No channels to remove from.</Trans>
            </span>
          ) : (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 4,
                maxHeight: 160,
                overflowY: 'auto',
                marginBottom: 8,
              }}
            >
              {channels.map((ch) => {
                const done = removedChannels.has(ch.id);
                return (
                  <div
                    key={ch.id}
                    style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}
                  >
                    {done ? (
                      <span role="status" aria-label={t(i18n)`Removed`}>
                        <CheckCircleFilled
                          aria-hidden="true"
                          style={{ color: 'var(--color-success, #22c55e)', fontSize: 14 }}
                        />
                      </span>
                    ) : removing ? (
                      <span role="status" aria-label={t(i18n)`Removing…`}>
                        <LoadingOutlined
                          aria-hidden="true"
                          style={{ fontSize: 14, color: 'var(--text-secondary)' }}
                        />
                      </span>
                    ) : (
                      <span style={{ width: 14, height: 14, display: 'inline-block' }} />
                    )}
                    <span
                      style={{
                        color: done ? 'var(--text-tertiary, #999)' : undefined,
                        textDecoration: done ? 'line-through' : undefined,
                      }}
                    >
                      #{ch.name}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Step 2: Stop the agent process */}
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 10,
          marginTop: 12,
          marginBottom: 8,
        }}
      >
        {step2Done ? (
          <>
            <span style={BADGE_DONE}>&#10003;</span>
            <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--color-success, #22c55e)' }}>
              {step2DoneLabel}
            </span>
          </>
        ) : isHostedAgent && step1Done ? (
          <>
            <span style={BADGE_ACTIVE}>2</span>
            <span style={{ fontSize: 13, fontWeight: 500 }}>{step2Label}</span>
            <button
              className="btn btn-sm"
              style={{ marginLeft: 'auto', fontSize: 11, padding: '2px 10px' }}
              disabled={deleting || hostedDeletePending}
              onClick={handleDelete}
            >
              {deleting
                ? t(i18n)`Deleting...`
                : hostedDeletePending
                  ? t(i18n)`Refreshing...`
                  : t(i18n)`Delete`}
            </button>
          </>
        ) : step1Done ? (
          <>
            <span style={BADGE_ACTIVE}>2</span>
            <span style={{ fontSize: 13, fontWeight: 500 }}>{step2Label}</span>
          </>
        ) : (
          <>
            <span style={BADGE_FUTURE}>2</span>
            <span style={{ fontSize: 13, color: 'var(--text-tertiary, #999)' }}>{step2Label}</span>
          </>
        )}
      </div>
      {step1Done && !step2Done && (
        <div style={{ marginLeft: 28 }}>
          {isHostedAgent ? (
            <>
              <p style={{ fontSize: 12, marginBottom: 8, color: 'var(--text-secondary)' }}>
                <Trans>
                  Delete <b>{agentName}</b> to revoke its credentials, disconnect it, and nudge{' '}
                  <b>{hostedMachineLabel}</b> to remove the hosted runtime.
                </Trans>
              </p>
              <p style={{ fontSize: 12, marginBottom: 8, color: 'var(--text-secondary)' }}>
                <Trans>
                  Confirm the delete here, then Borgee refreshes the hosted-agent list for{' '}
                  <b>{hostedMachineLabel}</b> before moving this flow to Done.
                </Trans>
              </p>
            </>
          ) : (
            <>
              <p style={{ fontSize: 12, marginBottom: 8, color: 'var(--text-secondary)' }}>
                {i18n._(copy.instruction(agentName))}
              </p>
              {hasPlatformDiff && (
                <Tabs
                  activeKey={platform}
                  onChange={(key: string) => setPlatform(key as Platform)}
                  size="small"
                  style={{ marginBottom: 4 }}
                  items={[
                    { key: 'windows', label: 'Windows' },
                    { key: 'linux', label: 'Linux / macOS' },
                  ]}
                />
              )}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 8 }}>
                {disconnectParts.map((cmd, i) => (
                  <div
                    key={i}
                    style={{
                      position: 'relative',
                      background: 'var(--bg-secondary, #f5f5f5)',
                      borderRadius: 6,
                      padding: 10,
                      fontSize: 12,
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-all',
                      lineHeight: 1.5,
                      fontFamily: 'monospace',
                    }}
                  >
                    {cmd}
                    <button
                      className="btn btn-sm"
                      style={{
                        position: 'absolute',
                        top: 6,
                        right: 6,
                        fontSize: 11,
                        padding: '2px 6px',
                      }}
                      onClick={() => copyToClipboard(cmd.replace(/^#.*\n/gm, '').trim())}
                      aria-label={t(i18n)`Copy command`}
                    >
                      <CopyOutlined aria-hidden="true" />
                    </button>
                  </div>
                ))}
              </div>
            </>
          )}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              fontSize: 13,
              color: 'var(--text-secondary)',
            }}
          >
            <LoadingOutlined aria-hidden="true" style={{ fontSize: 14 }} />
            <span>
              {hostedDeletePending
                ? waitingCopy
                : isHostedAgent
                  ? t(i18n)`Ready to delete ${agentName} and stop hosting on ${hostedMachineLabel}.`
                  : waitingCopy}
            </span>
          </div>
          <p style={{ fontSize: 11, color: 'var(--text-tertiary, #999)', marginTop: 8 }}>
            {isHostedAgent
              ? t(i18n)`This will not delete the computer or affect other hosted agents on it.`
              : i18n._(copy.skipHint)}
          </p>
        </div>
      )}

      {isHostedAgent && (
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 10,
            marginTop: 12,
            marginBottom: 16,
          }}
        >
          {step3Done ? (
            <>
              <span style={BADGE_DONE}>&#10003;</span>
              <span
                style={{ fontSize: 13, fontWeight: 500, color: 'var(--color-success, #22c55e)' }}
              >
                <Trans>Done</Trans>
              </span>
            </>
          ) : step2Done ? (
            <>
              <span style={BADGE_ACTIVE}>3</span>
              <span style={{ fontSize: 13, fontWeight: 500 }}>
                <Trans>Refreshing hosted status</Trans>
              </span>
            </>
          ) : (
            <>
              <span style={BADGE_FUTURE}>3</span>
              <span style={{ fontSize: 13, color: 'var(--text-tertiary, #999)' }}>
                <Trans>Done</Trans>
              </span>
            </>
          )}
        </div>
      )}

      {!isHostedAgent && (
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 10,
            marginTop: 12,
            marginBottom: 16,
          }}
        >
          {step2Done ? (
            <>
              <span style={BADGE_ACTIVE}>3</span>
              <span style={{ fontSize: 13, fontWeight: 500 }}>
                <Trans>Delete agent</Trans>
              </span>
            </>
          ) : (
            <>
              <span style={BADGE_FUTURE}>3</span>
              <span style={{ fontSize: 13, color: 'var(--text-tertiary, #999)' }}>
                <Trans>Delete agent</Trans>
              </span>
            </>
          )}
        </div>
      )}

      <button
        className="btn btn-sm"
        style={{
          width: '100%',
          ...(isHostedAgent
            ? step3Done
              ? {
                  background: 'var(--accent-primary, #5865f2)',
                  color: '#fff',
                  borderColor: 'var(--accent-primary, #5865f2)',
                }
              : { color: 'var(--text-secondary)' }
            : step2Done
              ? {
                  background: 'var(--text-danger, #d32f2f)',
                  color: '#fff',
                  borderColor: 'var(--text-danger, #d32f2f)',
                }
              : { color: 'var(--text-secondary)' }),
        }}
        disabled={deleting || (isHostedAgent ? false : !step1Done) || hostedDeletePending}
        onClick={
          isHostedAgent
            ? step3Done
              ? handleHostedDone
              : onClose
            : step2Done
              ? handleDelete
              : step1Done
                ? handleDelete
                : onClose
        }
      >
        {isHostedAgent
          ? deleting
            ? t(i18n)`Deleting Hosted Agent...`
            : step3Done
              ? t(i18n)`Done`
              : hostedDeletePending
                ? t(i18n)`Refreshing hosted status...`
                : step2Done
                  ? t(i18n)`Waiting for hosted status...`
                  : t(i18n)`I'll do this later`
          : deleting
            ? t(i18n)`Deleting...`
            : step2Done
              ? t(i18n)`Delete Agent`
              : step1Done
                ? t(i18n)`Skip, delete anyway`
                : t(i18n)`Skip, I'll do this later`}
      </button>
    </DecommissionModalSurface>
  );
}
